import { ConfirmationService, MenuItem } from 'primeng/api';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { EXERCISEPART_RATING_START_OPTIONS } from 'projects/core/src/lib/constants';
import { ExerciseModel } from 'projects/core/src/lib/models/exercise.model';
import {
  faStreetView,
  faUsers,
} from '@fortawesome/pro-light-svg-icons';
import { ExercisepartModel } from 'projects/core/src/lib/models/exercisepart.model';
import { TranslocoService } from '@ngneat/transloco';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';

@Component({
  selector: 'app-exercise-parts',
  templateUrl: './exercise-parts.component.html',
  styleUrls: ['./exercise-parts.component.scss']
})
export class ExercisePartsComponent implements OnInit {

  @Input() exercise: ExerciseModel = null;
  @Output() exerciseChange = new EventEmitter<ExerciseModel>();
  @Output() setEdit = new EventEmitter<string>();

  private formTypes = {
    normal: this.fb.group({
      name: new UntypedFormControl(null, [Validators.required]),
      duration: new UntypedFormControl(null, [Validators.required]),
      relevance: this.fb.group({
        for_candidate: new UntypedFormControl(true),
        for_observer: new UntypedFormControl(true),
      }, {
        validator: this.relevanceValidator
      }),
      rating_start: new UntypedFormControl('none'),
      rating_duration: new UntypedFormControl(null),
    }, {
      validator: this.ratingDurationValidator
    }),
    before: this.fb.group({
      name: new UntypedFormControl(null, [Validators.required]),
      unlock_days: new UntypedFormControl(null, [Validators.required, Validators.min(1), Validators.max(60)]),
    })
  };

  public inEdit = false;
  public inOrdering = false;
  public editExercisepart: ExercisepartModel = null;
  public editType: string;
  public submitting = false;
  public optionsRatingStart: Array<any> = [];
  public form: UntypedFormGroup = null;
  public items: Array<MenuItem> = [];
  private tempOrder: Array<number> = [];

  // icons
  public faStreetView = faStreetView;
  public faUsers = faUsers;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private auth: AuthStateService,
    private confirmation: ConfirmationService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.initItems();
    this.optionsRatingStart = FormHelper.getSelectList(EXERCISEPART_RATING_START_OPTIONS, null, this.transloco);
    this.formTypes['normal'].get('relevance.for_candidate').valueChanges.subscribe((newValue: any) => {
      if (newValue === false && this.formTypes['normal'].get('relevance.for_observer').value === false) {
        this.formTypes['normal'].get('relevance.for_candidate').setValue(true);
      }
    });
    this.formTypes['normal'].get('relevance.for_observer').valueChanges.subscribe((newValue: any) => {
      if (newValue === false && this.formTypes['normal'].get('relevance.for_candidate').value === false) {
        this.formTypes['normal'].get('relevance.for_observer').setValue(true);
      }
    });
    this.formTypes['normal'].get('rating_start').valueChanges.subscribe((newValue: any) => {
      if (newValue === 'start' || newValue === 'end') {
        this.formTypes['normal'].get('rating_duration').enable();
      } else {
        this.formTypes['normal'].get('rating_duration').setValue(null);
        this.formTypes['normal'].get('rating_duration').disable();
      }
    });
    this.ui.setFocus();
  }

  private initItems(): void {
    this.items = [];
    if (this.auth.hasRight('admin.exercisepart.create')) {
      this.items.push({
        label: this.transloco.translate('common.labels.normal'),
        icon: 'pi pi-sitemap',
        command: () => {
          this.onNew('normal');
        }
      });
      this.items.push({
        label: this.transloco.translate('common.labels.before'),
        icon: 'pi pi-calendar',
        command: () => {
          this.onNew('before');
        }
      });
    }
  }

  onNew(type: string): void {
    this.editType = type;
    this.form = this.formTypes[this.editType];
    this.clearForm();
    this.editExercisepart = null;
    this.startEdit();
  }

  onEdit(exercisepart: ExercisepartModel): void {
    if (this.auth.hasRight('admin.exercisepart.edit')) {
      this.editExercisepart = exercisepart;
      this.editType = this.editExercisepart.type;
      this.form = this.formTypes[this.editType];
      if (this.editType === 'before') {
        this.form.patchValue({
          name: exercisepart.name,
          unlock_days: exercisepart.unlock_days,
        });
      } else {
        this.form.patchValue({
          name: exercisepart.name,
          duration: exercisepart.duration,
          relevance: {
            for_candidate: exercisepart.for_candidate,
            for_observer: exercisepart.for_observer,
          },
          rating_start: exercisepart.rating_start,
          rating_duration: exercisepart.rating_duration,
        });
      }
      this.startEdit();
    }
  }

  onDelete(exercisepart: ExercisepartModel): void {
    if (this.auth.hasRight('admin.exercisepart.delete')) {
      this.confirmation.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: exercisepart.name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteModel('exerciseparts', exercisepart.id).subscribe({
            next: (result: any) => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: exercisepart.name }), this.transloco.translate('common.deleted'));
              this.exercise = new ExerciseModel().deserialize(result.data);
              this.exerciseChange.emit(this.exercise);
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  onStartOrdering(): void {
    if (this.auth.hasRight('admin.exercise.edit')) {
      this.inOrdering = true;
      this.tempOrder = this.exercise.exerciseparts.map((exercisepart: ExercisepartModel) => exercisepart.id);
      this.setEdit.emit('parts');
    }
  }

  onRowReorder(event: any): void {
    const ids = this.tempOrder.splice(event.dragIndex, 1);
    if (ids && ids.length > 0) {
      this.tempOrder.splice(event.dropIndex, 0, ids[0]);
    }
  }

  onCancel(): void {
    this.stopEdit();
    this.ui.hideMessage();
  }

  onSubmit(): void {
    if (this.exercise !== null) {
      this.ui.hideMessage();
      if (this.inOrdering === true) {
        this.submitting = true;
        const payload = {
          order: this.tempOrder
        };
        this.api.saveExercisepartsOrder(this.exercise.id, payload).subscribe({
          next: (result: any) => {
            this.ui.toastSuccess(this.transloco.translate('exercise_parts.sequence_saved'), this.transloco.translate('common.saved'));
            this.exercise = new ExerciseModel().deserialize(result.data);
            this.exerciseChange.emit(this.exercise);
            this.tempOrder = [];
            this.stopEdit();
          },
          error: (error: any) => {
            this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
          }
        }).add(() => {
          this.submitting = false;
        });
      } else if (this.inEdit === true) {
        if (this.form.valid === true) {
          this.submitting = true;
          const payload: any = {
            type: this.editType,
            exercise: this.exercise.id,
            name: this.form.get('name').value,
          };
          if (this.editType === 'before') {
            payload.unlock_days = this.form.get('unlock_days').value;
          } else {
            payload.duration =  this.form.get('duration').value;
            payload.for_candidate = this.form.get('relevance.for_candidate').value;
            payload.for_observer = this.form.get('relevance.for_observer').value;
            payload.rating_start = (this.form.get('rating_start').value !== null) ? this.form.get('rating_start').value : 'none';
            payload.rating_duration = this.form.get('rating_duration').value;
          }
          this.api
            .saveModel(
              'exerciseparts',
              this.editExercisepart !== null ? this.editExercisepart.id : null,
              payload
            )
            .subscribe(
              (result: any) => {
                this.ui.toastSuccess(
                  this.transloco.translate('common.messages.saved', { value: this.form.value.name }),
                  this.transloco.translate('common.saved')
                );
                this.exercise = new ExerciseModel().deserialize(result.data);
                this.exerciseChange.emit(this.exercise);
                this.stopEdit();
              },
              (error: any) => {
                this.ui.showMessage('error', this.transloco.translate(error.message), this.transloco.translate('common.error'));
                FormHelper.setBackendErrors(this.form, error.fielderrors);
              }
            )
            .add(() => {
              this.submitting = false;
            });
        } else {
          this.ui.showMessage(
            'error',
            this.transloco.translate('common.error_on_form_please_correct'),
            this.transloco.translate('common.error')
          );
          FormHelper.markFormAsDirty(this.form);
        }
      }
    }
  }


  startEdit(): void {
    this.inEdit = true;
    this.setEdit.emit('parts');
    this.ui.setFormFocus();
  }

  stopEdit(): void {
    this.inEdit = false;
    this.inOrdering = false;
    this.editType = null;
    this.clearForm();
    this.resetOrder();
    this.setEdit.emit(null);
    this.ui.setFocus();
  }

  private clearForm(): void {
    if (this.form !== null) {
      this.form.reset();
      if (this.editType === 'before') {
        this.form.patchValue({
          name: null,
          unlock_days: null,
        });
      } else {
        this.form.patchValue({
          name: null,
          duration: null,
          relevance: {
            for_candidate: true,
            for_observer: true,
          },
          rating_start: 'none',
          rating_duration: null,
        });
      }
    }
  }

  private resetOrder(): void {
    this.exercise.exerciseparts.sort((a: any, b: any) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));
  }

  private relevanceValidator(control: UntypedFormGroup): any {
    let result = { custom: true };
    if (control) {
      Object.keys(control.value).forEach((key: any) => {
        if (control.value[key] === true) {
          result = null;
        }
      });
    }
    return result;
  }

  private ratingDurationValidator(control: UntypedFormGroup): any {
    // if (control) {
    //   const start = control.get('rating_start').value;
    //   const duration = control.get('rating_duration').value;
    //   if (start !== null && start !== 'none' && (duration === null || duration === '')) {
    //     return { required: true };
    //   }
    // }
    return null;
  }

}
